import React, { useState, useEffect } from "react";
import { Box, TextField, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useModal } from "../ModalProvider";
import { sessionsService } from "../../../services/sessionsService";
import {
  pauseInactivityTimer,
  resumeInactivityTimer,
} from "../../../hooks/useInactivityTimer";
import { colors } from "../../../theme/colors";
import {
  SaveSessionContainer,
  SaveSessionTitle,
  SaveSessionSubtitle,
  InputLabel,
  ButtonsContainer,
  CancelButton,
  SaveButton,
  WarningTitle,
  WarningMessage,
} from "./SaveSessionModal.styled";

interface SaveSessionModalProps {
  sessionId: number;
  sessionType: "treatment" | "consult";
  patientId: string;
  duration: number;
  audioBlob: Blob | null;
}

const SaveSessionModal: React.FC<SaveSessionModalProps> = ({
  sessionId,
  sessionType,
  patientId: _patientId,
  duration: _duration,
  audioBlob: _audioBlob,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { closeModal } = useModal();
  const [diagnosis, setDiagnosis] = useState("");
  const [note, setNote] = useState("");
  const [showCancelWarning, setShowCancelWarning] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Pause inactivity timer while saving session
  useEffect(() => {
    pauseInactivityTimer();
    return () => {
      resumeInactivityTimer();
    };
  }, []);

  const handleDiagnosisChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDiagnosis(event.target.value);
    setHasChanges(true);
  };

  const handleNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNote(event.target.value);
    setHasChanges(true);
  };

  const handleCancel = () => {
    if (hasChanges && !showCancelWarning) {
      setShowCancelWarning(true);
      return;
    }
    closeModal("save-session-modal");
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      // Save session with notes and diagnosis
      await sessionsService.saveSession(sessionId, {
        notes: note || undefined,
        preliminaryDiagnosis:
          sessionType === "consult" ? diagnosis || undefined : undefined,
      });

      console.log("Session saved successfully:", sessionId);

      // Close modal
      closeModal("save-session-modal");

      // Navigate to session details page
      navigate(`/sessions/${sessionId}`);
    } catch (error) {
      console.error("Error saving session:", error);
      // TODO: Show error message to user
      setIsSaving(false);
    }
  };

  const isValid = sessionType === "consult" ? diagnosis.trim() !== "" : true;

  return (
    <SaveSessionContainer>
      <SaveSessionTitle>{t("saveSession.title")}</SaveSessionTitle>
      <SaveSessionSubtitle>{t("saveSession.subtitle")}</SaveSessionSubtitle>

      {sessionType === "consult" && (
        <Box sx={{ marginBottom: "24px", width: "100%" }}>
          <InputLabel>
            {t("saveSession.preliminaryDiagnosis")}{" "}
            <Box component="span" sx={{ color: "#D32F2F" }}>
              *
            </Box>
          </InputLabel>
          <TextField
            fullWidth
            placeholder={t("saveSession.preliminaryDiagnosisPlaceholder")}
            value={diagnosis}
            onChange={handleDiagnosisChange}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#FFFFFF",
                "& fieldset": {
                  borderColor: colors.border.light,
                },
                "&:hover fieldset": {
                  borderColor: colors.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary.main,
                },
              },
            }}
          />
        </Box>
      )}

      <Box sx={{ marginBottom: "24px", width: "100%" }}>
        <InputLabel>{t("saveSession.note")}</InputLabel>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder={t("saveSession.notePlaceholder")}
          value={note}
          onChange={handleNoteChange}
          variant="outlined"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "#FFFFFF",
              "& fieldset": {
                borderColor: colors.border.light,
              },
              "&:hover fieldset": {
                borderColor: colors.primary.main,
              },
              "&.Mui-focused fieldset": {
                borderColor: colors.primary.main,
              },
            },
          }}
        />
      </Box>

      {showCancelWarning && (
        <Box sx={{ marginBottom: "16px", width: "100%" }}>
          <WarningTitle>{t("saveSession.cancelWarning.title")}</WarningTitle>
          <WarningMessage>
            {t("saveSession.cancelWarning.message")}
          </WarningMessage>
        </Box>
      )}

      <ButtonsContainer>
        <CancelButton onClick={handleCancel} disabled={isSaving}>
          {t("common.cancel")}
        </CancelButton>
        <SaveButton onClick={handleSave} disabled={!isValid || isSaving}>
          {t("saveSession.saveButton")}
          {isSaving && (
            <CircularProgress size={20} sx={{ marginLeft: 1 }} color="inherit" />
          )}
        </SaveButton>
      </ButtonsContainer>
    </SaveSessionContainer>
  );
};

export default SaveSessionModal;
